import type { UserProfile } from "../types/profile";
import type { SilverConnectRegisterProfileRequest, SilverConnectUpdateProfileRequest, ProfileImageDto } from "../../../types";
import { apiClient, API_ENDPOINTS, ApiError } from "../../../config/api";

export async function getUserProfile(): Promise<UserProfile> {
  const response = await apiClient.get<{ result: UserProfile; resultType: string }>(
    API_ENDPOINTS.PROFILE.GET
  );

  if (response.resultType !== 'SUCCESS') {
    throw new Error("프로필 정보를 불러올 수 없습니다.");
  }

  return response.result;
}

export async function updateUserProfile(data: SilverConnectUpdateProfileRequest): Promise<UserProfile> {
  const response = await apiClient.put<{ result: UserProfile; resultType: string }>(
    API_ENDPOINTS.PROFILE.UPDATE,
    data
  );

  if (response.resultType !== 'SUCCESS') {
    throw new Error("프로필 수정에 실패했습니다.");
  }

  return response.result;
}

export interface ResubmitResponse {
  status: string;
  resubmissionCount: number;
  remainingChances: number;
}

export type ResubmitErrorCode =
  | 'ALREADY_PENDING'
  | 'PROFILE_NOT_MODIFIED'
  | 'MAX_RESUBMISSION_EXCEEDED'
  | 'RESUBMIT_NOT_REJECTED_STATUS';

export class ResubmitError extends Error {
  constructor(
    message: string,
    public readonly code: ResubmitErrorCode | string,
    public readonly httpStatus: number,
  ) {
    super(message);
    this.name = 'ResubmitError';
  }
}

export async function requestResubmission(): Promise<ResubmitResponse> {
  try {
    const response = await apiClient.post<{ result: ResubmitResponse; resultType: string }>(
      API_ENDPOINTS.PROFILE.RESUBMIT
    );

    if (response.resultType !== 'SUCCESS') {
      throw new ResubmitError("재심사 요청에 실패했습니다.", 'UNKNOWN', 500);
    }

    return response.result;
  } catch (err) {
    if (err instanceof ApiError) {
      const code = (err.errorType ?? err.errorCode ?? 'UNKNOWN') as ResubmitErrorCode;
      throw new ResubmitError(err.message, code, err.status);
    }
    throw err;
  }
}

// ---- 정회원 프로필 수정: 자유 필드 (즉시 반영) ----

export interface UpdateFreeFieldsRequest {
  interestIds: number[];
  preferredAge: string;
  residenceRegion: { level1Id: number; level2Id: number };
  preferredRegions: Array<{ level1Id: number; level2Id: number }>;
}

export async function updateFreeFields(data: UpdateFreeFieldsRequest): Promise<void> {
  const response = await apiClient.put<{ resultType: string }>(
    API_ENDPOINTS.PROFILE.UPDATE_FREE_FIELDS,
    data
  );
  if (response.resultType !== 'SUCCESS') {
    throw new Error("저장에 실패했습니다.");
  }
}

// ---- 정회원 프로필 수정: 심사 필드 (심사 후 반영) ----

export interface SubmitReviewFieldsRequest {
  jobTitle?: string | null;
  oneLiner?: string | null;
  selfIntro?: string | null;
  profileImages?: ProfileImageDto[];
  deletedStorageKeys?: string[];
}

export async function submitReviewFields(data: SubmitReviewFieldsRequest): Promise<void> {
  const response = await apiClient.post<{ resultType: string }>(
    API_ENDPOINTS.PROFILE.SUBMIT_REVIEW_FIELDS,
    data
  );
  if (response.resultType !== 'SUCCESS') {
    throw new Error("심사 요청에 실패했습니다.");
  }
}